import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getCommDirectory, getCommDirectoryGroups, createCommDirectoryEntry, updateCommDirectoryEntry,
  deleteCommDirectoryEntry, downloadCommDirectoryTemplate, importCommDirectory,
  getFrequencyUpdates, getFrequencyUpdate, createFrequencyUpdate, updateFrequencyUpdate,
  deleteFrequencyUpdate, publishFrequencyUpdate, previewFrequencyUpdate,
  getFrequencyUpdatesAnalytics, getFrequencyUpdateAnalytics,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Users2, Plus, Pencil, Trash2, Upload, Download, Search, Send, Eye, BarChart3,
  Megaphone, Link2, X, Paperclip, CheckCircle2, ChevronLeft, Mail, MessageCircle, FileText,
} from 'lucide-react';

const STATUS_BADGE = {
  Active: 'bg-emerald-100 text-emerald-700',
  Inactive: 'bg-gray-200 text-gray-600',
  Draft: 'bg-gray-200 text-gray-800',
  Published: 'bg-emerald-100 text-emerald-700',
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ====================================================================
// COMMUNICATION DIRECTORY (Super Admin)
// ====================================================================
export function CommDirectoryPage() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', mobile: '', department: '', designation: '', status: 'Active' });
  const fileRef = useRef();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (deptFilter !== 'all') params.department = deptFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [r, g] = await Promise.all([getCommDirectory(params), getCommDirectoryGroups()]);
      setRows(r.data);
      setGroups(g.data.groups || []);
    } catch { toast.error('Failed to load directory'); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [deptFilter, statusFilter]);

  const openCreate = () => { setEditing(null); setForm({ full_name: '', email: '', mobile: '', department: '', designation: '', status: 'Active' }); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ full_name: row.full_name || '', email: row.email || '', mobile: row.mobile || '', department: row.department || '', designation: row.designation || '', status: row.status || 'Active' }); setDialogOpen(true); };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error('Employee name is required'); return; }
    if (!form.email.trim() && !form.mobile.trim()) { toast.error('Provide at least Email or Mobile Number'); return; }
    if (!form.department.trim()) { toast.error('Department is required'); return; }
    try {
      if (editing) { await updateCommDirectoryEntry(editing.id, form); toast.success('Recipient updated'); }
      else { await createCommDirectoryEntry(form); toast.success('Recipient added'); }
      setDialogOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.full_name} from the directory?`)) return;
    try { await deleteCommDirectoryEntry(row.id); toast.success('Removed'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await downloadCommDirectoryTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'communication_directory_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await importCommDirectory(file);
      toast.success(`Imported ${res.data.imported} new, updated ${res.data.updated}`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} row(s) skipped`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="comm-directory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold flex items-center gap-2"><Users2 className="h-5 w-5 text-[#2563EB]" /> Employee Communication Directory</h2>
          <p className="text-sm text-muted-foreground">Recipient master for email & WhatsApp. Departments here become your communication groups.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="dir-template-btn"><Download className="h-4 w-4 mr-1" />Template</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onImport} data-testid="dir-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="dir-import-btn"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <Button onClick={openCreate} data-testid="dir-add-btn"><Plus className="h-4 w-4 mr-1" />Add Recipient</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {groups.map(g => (
          <Badge key={g.department} variant="secondary" className="text-xs" data-testid={`dir-group-${g.department}`}>{g.department}: {g.count}</Badge>
        ))}
        {groups.length === 0 && <span className="text-xs text-muted-foreground">No active recipients yet.</span>}
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search name, email, mobile…" value={search}
                onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchData()} data-testid="dir-search" />
            </div>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[180px]" data-testid="dir-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {groups.map(g => <SelectItem key={g.department} value={g.department}>{g.department}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="dir-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Mobile</th>
                  <th className="p-2">Department</th><th className="p-2">Designation</th><th className="p-2">Status</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody data-testid="dir-table-body">
                {loading ? (
                  <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No recipients. Add or import some.</td></tr>
                ) : rows.map(row => (
                  <tr key={row.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`dir-row-${row.id}`}>
                    <td className="p-2 font-medium">{row.full_name}</td>
                    <td className="p-2">{row.email || '—'}</td>
                    <td className="p-2">{row.mobile || '—'}</td>
                    <td className="p-2">{row.department}</td>
                    <td className="p-2">{row.designation || '—'}</td>
                    <td className="p-2"><Badge className={STATUS_BADGE[row.status] || ''}>{row.status}</Badge></td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`dir-edit-${row.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(row)} data-testid={`dir-delete-${row.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="dir-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee Name *</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="dir-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="dir-form-email" /></div>
              <div><Label>Mobile Number</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="dir-form-mobile" /></div>
            </div>
            <p className="text-xs text-muted-foreground">At least one of Email or Mobile is required.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department *</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g. Sales" data-testid="dir-form-dept" /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} data-testid="dir-form-designation" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="dir-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="dir-form-save">{editing ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====================================================================
// UPDATES LIST
// ====================================================================
export function UpdatesListPage() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pubTarget, setPubTarget] = useState(null);
  const navigate = useNavigate();

  const fetchUpdates = async () => {
    setLoading(true);
    try { const r = await getFrequencyUpdates(); setUpdates(r.data); } catch { toast.error('Failed to load updates'); }
    setLoading(false);
  };
  useEffect(() => { fetchUpdates(); }, []);

  const doPublish = async () => {
    const u = pubTarget; if (!u) return;
    try { const r = await publishFrequencyUpdate(u.id); toast.success(`Published — ${r.data.recipients} recipient(s) (Email: ${r.data.email}, WhatsApp: ${r.data.whatsapp})`); setPubTarget(null); fetchUpdates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); setPubTarget(null); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete draft "${u.title}"?`)) return;
    try { await deleteFrequencyUpdate(u.id); toast.success('Deleted'); fetchUpdates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="updates-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#FF6B6B]" /> Updates</h2>
          <p className="text-sm text-muted-foreground">Create an update, pick target departments, and publish directly.</p>
        </div>
        <Button onClick={() => navigate('/frequency/updates/new')} data-testid="updates-new-btn"><Plus className="h-4 w-4 mr-1" />New Update</Button>
      </div>

      {loading ? <div className="text-center py-12 text-muted-foreground">Loading…</div> :
        updates.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-muted-foreground" data-testid="updates-empty">No updates yet. Click "New Update" to create your first one.</CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {updates.map(u => (
              <Card key={u.id} data-testid={`update-card-${u.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className={`min-w-0 ${u.status === 'Published' ? 'cursor-pointer' : ''}`}
                    onClick={() => u.status === 'Published' && navigate(`/frequency/updates/${u.id}/analytics`)}
                    data-testid={`update-open-${u.id}`}>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate hover:underline">{u.title}</p>
                      <Badge className={STATUS_BADGE[u.status] || ''}>{u.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {u.source_department} · {(u.target_departments || []).join(', ') || 'No targets'} · {(u.channels || []).join(' + ')}
                      {u.published_at ? ` · sent ${new Date(u.published_at).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    {u.status === 'Published' && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/frequency/updates/${u.id}/analytics`)} data-testid={`update-analytics-${u.id}`}><BarChart3 className="h-4 w-4 mr-1" />Analytics</Button>
                    )}
                    {u.status !== 'Published' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/frequency/updates/${u.id}`)} data-testid={`update-edit-${u.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                        <Button size="sm" onClick={() => setPubTarget(u)} data-testid={`update-publish-${u.id}`}><Send className="h-4 w-4 mr-1" />Publish</Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(u)} data-testid={`update-delete-${u.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!pubTarget} onOpenChange={(o) => !o && setPubTarget(null)}>
        <DialogContent data-testid="list-confirm-publish-dialog">
          <DialogHeader><DialogTitle>Publish &amp; Send</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Send "{pubTarget?.title}" to all active recipients in {(pubTarget?.target_departments || []).includes('All') ? 'all departments' : (pubTarget?.target_departments || []).join(', ')} via {(pubTarget?.channels || []).join(' + ')}?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPubTarget(null)}>Cancel</Button>
            <Button onClick={doPublish} data-testid="list-confirm-publish-btn"><Send className="h-4 w-4 mr-1" />Publish &amp; Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// ====================================================================
export function UpdateEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ title: '', content: '', urls: [], images: [], attachments: [], target_departments: [], channels: ['email', 'whatsapp'] });
  const [urlInput, setUrlInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [draftId, setDraftId] = useState(isNew ? null : id);
  const fileRef = useRef();

  useEffect(() => { (async () => {
    try { const g = await getCommDirectoryGroups(); setGroups(g.data.groups || []); } catch { /* contributor still sees */ }
    if (!isNew) {
      try { const r = await getFrequencyUpdate(id); const u = r.data; const uc = u.update_content || {};
        setForm({ title: u.title || '', content: uc.content || '', urls: uc.urls || [], images: uc.images || [], attachments: uc.attachments || [], target_departments: u.target_departments || [], channels: u.channels || ['email', 'whatsapp'] });
      } catch { toast.error('Could not load update'); navigate('/frequency/updates'); }
    }
  })(); /* eslint-disable-next-line */ }, [id]);

  const toggleDept = (dept) => setForm(f => {
    const set = new Set(f.target_departments);
    if (dept === 'All') { return { ...f, target_departments: set.has('All') ? [] : ['All'] }; }
    set.delete('All');
    if (set.has(dept)) set.delete(dept); else set.add(dept);
    return { ...f, target_departments: Array.from(set) };
  });
  const toggleChannel = (ch) => setForm(f => {
    const set = new Set(f.channels);
    if (set.has(ch)) set.delete(ch); else set.add(ch);
    return { ...f, channels: Array.from(set) };
  });
  const addUrl = () => { const u = urlInput.trim(); if (!u) return; setForm(f => ({ ...f, urls: [...f.urls, u] })); setUrlInput(''); };
  const removeUrl = (i) => setForm(f => ({ ...f, urls: f.urls.filter((_, idx) => idx !== i) }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        const dataUrl = await fileToDataUrl(file);
        if (file.type.startsWith('image/')) setForm(f => ({ ...f, images: [...f.images, { name: file.name, data_url: dataUrl }] }));
        else setForm(f => ({ ...f, attachments: [...f.attachments, { name: file.name, url: dataUrl }] }));
      } catch { toast.error(`Could not read ${file.name}`); }
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const removeAttachment = (i) => setForm(f => ({ ...f, attachments: f.attachments.filter((_, idx) => idx !== i) }));

  const buildPayload = () => ({
    title: form.title, content: form.content, urls: form.urls, images: form.images, attachments: form.attachments,
    target_departments: form.target_departments, channels: form.channels,
  });

  const persist = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return null; }
    if (form.channels.length === 0) { toast.error('Select at least one channel'); return null; }
    setSaving(true);
    try {
      if (isNew) { const r = await createFrequencyUpdate(buildPayload()); setDraftId(r.data.id); toast.success('Draft saved'); return r.data.id; }
      await updateFrequencyUpdate(id, buildPayload()); toast.success('Saved'); return id;
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); return null; }
    finally { setSaving(false); }
  };

  const onSave = async () => { const newId = await persist(); if (newId && isNew) navigate(`/frequency/updates/${newId}`); };

  const onPreview = async () => {
    const savedId = await persist();
    if (!savedId) return;
    try { const r = await previewFrequencyUpdate(savedId); setPreviewHtml(r.data.html); setRecipientInfo(r.data); }
    catch { toast.error('Preview failed'); }
  };

  const onPublish = async () => {
    if (form.target_departments.length === 0) { toast.error('Select at least one target department'); return; }
    const savedId = await persist();
    if (!savedId) return;
    setConfirmPublish(true);
  };

  const doPublish = async () => {
    const targetId = draftId || (await persist());
    if (!targetId) { setConfirmPublish(false); return; }
    try { const r = await publishFrequencyUpdate(targetId); toast.success(`Published — ${r.data.recipients} recipient(s)`); setConfirmPublish(false); navigate('/frequency/updates'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); setConfirmPublish(false); }
  };

  return (
    <div className="space-y-6" data-testid="update-editor-page">
      <button onClick={() => navigate('/frequency/updates')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="update-back-btn"><ChevronLeft className="h-4 w-4" />Back to Updates</button>
      <h2 className="text-xl font-heading font-bold">{isNew ? 'New Update' : 'Edit Update'}</h2>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card><CardContent className="p-4 space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Update headline" data-testid="update-title" /></div>
            <div><Label>Update</Label><Textarea rows={8} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write your update…" data-testid="update-content" /></div>

            <div>
              <Label>URLs</Label>
              <div className="flex gap-2">
                <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl())} placeholder="https://…" data-testid="update-url-input" />
                <Button type="button" variant="outline" onClick={addUrl} data-testid="update-url-add"><Link2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.urls.map((u, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">{u}<X className="h-3 w-3 cursor-pointer" onClick={() => removeUrl(i)} /></Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Images & Attachments</Label>
              <input ref={fileRef} type="file" multiple hidden onChange={onFiles} data-testid="update-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="update-file-btn"><Paperclip className="h-4 w-4 mr-1" />Add files</Button>
              <div className="space-y-1 mt-2">
                {form.images.map((im, i) => (
                  <div key={`img${i}`} className="flex items-center justify-between text-sm bg-accent/40 rounded px-2 py-1"><span className="truncate">🖼️ {im.name}</span><X className="h-3.5 w-3.5 cursor-pointer" onClick={() => removeImage(i)} /></div>
                ))}
                {form.attachments.map((a, i) => (
                  <div key={`att${i}`} className="flex items-center justify-between text-sm bg-accent/40 rounded px-2 py-1"><span className="truncate">📎 {a.name}</span><X className="h-3.5 w-3.5 cursor-pointer" onClick={() => removeAttachment(i)} /></div>
                ))}
              </div>
            </div>
          </CardContent></Card>
        </div>

        <div className="space-y-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Share With Departments</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-64 overflow-y-auto" data-testid="update-audience">
              <label className="flex items-center gap-2 text-sm cursor-pointer font-medium">
                <Checkbox checked={form.target_departments.includes('All')} onCheckedChange={() => toggleDept('All')} data-testid="audience-all" />
                All Departments
              </label>
              <div className="h-px bg-border my-1" />
              {groups.length === 0 && <p className="text-xs text-muted-foreground">No groups yet. Add recipients to the directory.</p>}
              {groups.map(g => (
                <label key={g.department} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.target_departments.includes('All') || form.target_departments.includes(g.department)} disabled={form.target_departments.includes('All')} onCheckedChange={() => toggleDept(g.department)} data-testid={`audience-${g.department}`} />
                  {g.department} <span className="text-muted-foreground text-xs">({g.count})</span>
                </label>
              ))}
            </CardContent>
          </Card>

          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Channels</CardTitle></CardHeader>
            <CardContent className="space-y-2" data-testid="update-channels">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={form.channels.includes('email')} onCheckedChange={() => toggleChannel('email')} data-testid="channel-email" /><Mail className="h-4 w-4" /> Email</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={form.channels.includes('whatsapp')} onCheckedChange={() => toggleChannel('whatsapp')} data-testid="channel-whatsapp" /><MessageCircle className="h-4 w-4" /> WhatsApp</label>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={onSave} disabled={saving} data-testid="update-save-btn"><FileText className="h-4 w-4 mr-1" />Save Draft</Button>
            <Button variant="outline" onClick={onPreview} disabled={saving} data-testid="update-preview-btn"><Eye className="h-4 w-4 mr-1" />Preview</Button>
            <Button onClick={onPublish} disabled={saving} data-testid="update-publish-btn"><Send className="h-4 w-4 mr-1" />Publish & Send</Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <DialogContent data-testid="confirm-publish-dialog">
          <DialogHeader><DialogTitle>Publish & Send</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will send the update to all active recipients in {form.target_departments.includes('All') ? 'all departments' : form.target_departments.join(', ')} via {form.channels.join(' + ')}. Continue?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPublish(false)} data-testid="confirm-publish-cancel">Cancel</Button>
            <Button onClick={doPublish} data-testid="confirm-publish-btn"><Send className="h-4 w-4 mr-1" />Publish &amp; Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="update-preview-dialog">
          <DialogHeader><DialogTitle>Preview {recipientInfo ? `· ${recipientInfo.recipient_count} recipient(s)` : ''}</DialogTitle></DialogHeader>
          {recipientInfo && <p className="text-xs text-muted-foreground">Email: {recipientInfo.with_email} · WhatsApp: {recipientInfo.with_phone}</p>}
          <iframe title="preview" srcDoc={previewHtml} className="w-full h-[60vh] border rounded-lg bg-white" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====================================================================
// UPDATE ANALYTICS (single update drill-down)
// ====================================================================
export function UpdateAnalyticsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await getFrequencyUpdateAnalytics(id); setData(r.data); } catch { toast.error('Failed to load analytics'); }
  })(); }, [id]);

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  const stats = [
    { label: 'Sent', value: data.sent }, { label: 'Delivered', value: data.delivered },
    { label: 'Opened', value: data.opened }, { label: 'Acknowledged', value: data.acknowledged },
    { label: 'Queries Raised', value: data.queries_raised },
  ];
  return (
    <div className="space-y-6" data-testid="update-analytics-page">
      <button onClick={() => navigate('/frequency/updates')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-4 w-4" />Back to Updates</button>
      <div>
        <h2 className="text-xl font-heading font-bold">{data.update?.title}</h2>
        <p className="text-sm text-muted-foreground">{data.update?.source_department ? `${data.update.source_department} · ` : ''}{(data.update?.target_departments || []).join(', ')} · {(data.update?.channels || []).join(' + ')}{data.update?.published_at ? ` · sent ${new Date(data.update.published_at).toLocaleString()}` : ''}</p>
        {data.update?.created_by_name && <p className="text-xs text-muted-foreground mt-0.5">By {data.update.created_by_name} ({data.update.created_by_email})</p>}
      </div>

      {data.content_html && (
        <Card className="overflow-hidden" data-testid="update-detail-content">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" />Update Content</CardTitle></CardHeader>
          <CardContent className="p-0">
            <iframe srcDoc={data.content_html} title="update-content" className="w-full bg-white" style={{ height: '50vh', border: 0 }} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map(s => (
          <Card key={s.label} data-testid={`update-stat-${s.label}`}><CardContent className="p-4"><p className="text-2xl font-bold">{s.value}</p><p className="text-xs text-muted-foreground">{s.label}</p></CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4" />Recipients <span className="text-xs text-muted-foreground font-normal">— rows in red did not open via email or WhatsApp</span></CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Department</th><th className="p-2">Channel</th><th className="p-2">Opened</th><th className="p-2">Acknowledged</th><th className="p-2">Query Raised</th>
              </tr></thead>
              <tbody data-testid="update-recipients-body">
                {(data.recipients || []).map(r => (
                  <tr key={r.id} className={`border-b border-border/60 ${!r.engaged ? 'bg-rose-50' : ''}`} data-testid={`recipient-row-${r.id}`}>
                    <td className={`p-2 font-medium ${!r.engaged ? 'text-rose-600' : ''}`}>{r.recipient_name || '—'}</td>
                    <td className={`p-2 ${!r.engaged ? 'text-rose-600' : ''}`}>{r.recipient_email}</td>
                    <td className="p-2">{r.recipient_department || '—'}</td>
                    <td className="p-2">{r.channel}</td>
                    <td className="p-2">{(r.opened_at || r.click_at) ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : '—'}</td>
                    <td className="p-2">{r.acknowledged_at ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : '—'}</td>
                    <td className="p-2" data-testid={`recipient-query-${r.id}`}>{r.query_raised ? <span className="text-amber-600 font-medium" title={r.query_raised}>{r.query_count > 1 ? `${r.query_count} queries` : r.query_raised}</span> : '—'}</td>
                  </tr>
                ))}
                {(data.recipients || []).length === 0 && <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No recipients recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
